/**
 * Pure geo helpers for the trip timeline. We have no routing API
 * (pay-on-arrival product, Google Maps is an embed iframe that can't
 * draw routes), so walking distance is the straight-line haversine
 * distance and walking time is a flat-speed estimate. These are
 * deliberately approximate — they convey relative effort between
 * stops, not turn-by-turn precision.
 */

export interface LatLon {
  readonly lat: number;
  readonly lon: number;
}

const EARTH_RADIUS_M = 6_371_000;

/** Great-circle distance between two points, in metres. */
export function haversineMeters(a: LatLon, b: LatLon): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Estimated walking time in whole minutes at ~80 m/min (≈4.8 km/h),
 * the average urban walking pace. Always at least 1 minute so the
 * chip never reads "0 min" for adjacent stops.
 */
export function walkMinutes(meters: number): number {
  return Math.max(1, Math.round(meters / 80));
}

/**
 * Split a metre distance into a display value + unit so the caller
 * can localise the number (Arabic-Indic digits etc.) and pick the
 * right translated unit key. Under 1 km → whole metres; at or above
 * → kilometres rounded to one decimal.
 */
export function distanceParts(meters: number): {
  readonly value: number;
  readonly unit: "km" | "m";
} {
  if (meters >= 1000) {
    return { value: Math.round(meters / 100) / 10, unit: "km" };
  }
  return { value: Math.round(meters), unit: "m" };
}

/** Coordinates of an item, or null when it has no geocode yet. */
export function coordsOf(item: {
  readonly latitude: number | null;
  readonly longitude: number | null;
}): LatLon | null {
  if (item.latitude === null || item.longitude === null) return null;
  return { lat: item.latitude, lon: item.longitude };
}

/**
 * Google Maps walking-directions deep link between two points. This is
 * a plain maps URL (opened in a new tab), not the JS Maps API — no key,
 * no billing, no CSP changes.
 */
export function walkingDirectionsUrl(from: LatLon, to: LatLon): string {
  return (
    "https://www.google.com/maps/dir/?api=1" +
    `&origin=${from.lat},${from.lon}` +
    `&destination=${to.lat},${to.lon}` +
    "&travelmode=walking"
  );
}
